import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/Input';
import { metaAdsApi, type MetaInterest } from '@/services/api/metaAds';

interface InterestsSectionProps {
  adAccountId: string;
  interests: MetaInterest[];
  onChange: (interests: MetaInterest[]) => void;
}

/**
 * Interest typeahead — debounced search against `/meta/targeting-search`.
 *
 * 300ms debounce + React Query with `staleTime: 5min` keyed on the
 * normalized query. The backend already caps the result count at 50;
 * the UI surfaces the first 20 visible at a time. Selected interests
 * render as removable chips above the input.
 *
 * The backend route also layers a 60s in-memory TTL cache keyed on
 * (q.lower(), search_type, ad_account_id) — repeat queries within the
 * window (across concurrent users or wizard re-opens) skip Meta entirely.
 */
export const InterestsSection: React.FC<InterestsSectionProps> = ({
  adAccountId,
  interests,
  onChange,
}) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce — fire the search only after 300ms of typing inactivity.
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['meta-targeting-search', adAccountId, debouncedQuery],
    queryFn: () =>
      metaAdsApi.searchTargeting(adAccountId, debouncedQuery, 'adinterest'),
    enabled: debouncedQuery.length >= 2,
    staleTime: 5 * 60 * 1000,
  });

  const addInterest = (interest: MetaInterest) => {
    if (interests.some((i) => i.id === interest.id)) return;
    onChange([...interests, interest]);
    setQuery('');
    setDebouncedQuery('');
  };

  const removeInterest = (id: string) => {
    onChange(interests.filter((i) => i.id !== id));
  };

  // Hide already-selected interests from the suggestion list.
  const visibleResults = results.filter(
    (r) => !interests.some((i) => i.id === r.id),
  );

  return (
    <div className="space-y-3">
      <div>
        <p className="text-sm text-gray-600 mb-2">
          Search for interests Meta tracks (e.g. "running", "wedding planning").
          Optional — empty = no interest restriction.
        </p>
      </div>

      {interests.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {interests.map((i) => (
            <button
              key={i.id}
              type="button"
              onClick={() => removeInterest(i.id)}
              aria-label={`Remove ${i.name}`}
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {i.name} <span aria-hidden="true" className="ml-1">×</span>
            </button>
          ))}
        </div>
      )}

      <div>
        <label htmlFor="meta-interest-search" className="sr-only">
          Search interests
        </label>
        <Input
          id="meta-interest-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search interests…"
        />
      </div>

      {debouncedQuery.length >= 2 && (
        <div className="border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
          {isFetching && (
            <p className="text-xs text-gray-500 p-3">Searching…</p>
          )}
          {!isFetching && visibleResults.length === 0 && (
            <p className="text-xs text-gray-500 p-3">No matches.</p>
          )}
          {!isFetching && visibleResults.length > 0 && (
            <ul>
              {visibleResults.slice(0, 20).map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => addInterest(r)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 focus:outline-none focus:bg-blue-50 text-sm"
                  >
                    {r.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
